import { CameraOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, Descriptions, Drawer, Form, Input, InputNumber, Popconfirm, Row, Select, Space, Spin, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { nvrService } from "../services/nvr.service";
import { cameraService } from "@/modules/cameras/services/camera.service";

const { Title } = Typography;

export function NvrDetailPage() {
	const { id } = useParams();
	const nvrId = Number(id);
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	// Drawer state
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingCameraId, setEditingCameraId] = useState<number | null>(null);
	const [form] = Form.useForm();

	// ==================== NVR detail ====================
	const { data: nvrData, isLoading: nvrLoading } = useQuery({
		queryKey: ["nvr", id],
		queryFn: () => nvrService.get(nvrId),
		enabled: !!id,
	});

	// ==================== Kameralar ====================
	const { data: camerasData, isLoading: camerasLoading } = useQuery({
		queryKey: ["cameras", { nvr_id: nvrId }],
		queryFn: () => cameraService.list({ nvr_id: nvrId, per_page: 100 }),
		enabled: !!id,
	});

	// ==================== Mutations ====================
	const saveCameraMutation = useMutation({
		mutationFn: (values: Record<string, unknown>) => {
			const payload = { ...values, nvr_id: nvrId };
			return editingCameraId ? cameraService.update(editingCameraId, payload) : cameraService.create(payload);
		},
		onSuccess: () => {
			message.success(editingCameraId ? "Kamera yangilandi" : "Kamera qo'shildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["cameras", { nvr_id: nvrId }] });
			queryClient.invalidateQueries({ queryKey: ["nvr", id] });
		},
		onError: () => message.error("Xatolik"),
	});

	const deleteCameraMutation = useMutation({
		mutationFn: (cameraId: number) => cameraService.remove(cameraId),
		onSuccess: () => {
			message.success("Kamera o'chirildi");
			queryClient.invalidateQueries({ queryKey: ["cameras", { nvr_id: nvrId }] });
			queryClient.invalidateQueries({ queryKey: ["nvr", id] });
		},
	});

	const captureMutation = useMutation({
		mutationFn: (cameraId: number) => cameraService.capture(cameraId),
		onSuccess: () => message.success("Screenshot so'rov yuborildi"),
	});

	const syncMutation = useMutation({
		mutationFn: () => nvrService.sync(nvrId),
		onSuccess: () => {
			message.success("Sinxronlandi");
			queryClient.invalidateQueries({ queryKey: ["nvr", id] });
		},
	});

	// ==================== Drawer ====================
	const openCreateCamera = () => {
		setEditingCameraId(null);
		form.resetFields();
		setDrawerOpen(true);
	};

	const openEditCamera = (record: Record<string, unknown>) => {
		setEditingCameraId(record.id as number);
		form.setFieldsValue(record);
		setDrawerOpen(true);
	};

	const closeDrawer = () => {
		setDrawerOpen(false);
		setEditingCameraId(null);
		form.resetFields();
	};

	// ==================== Render ====================
	if (nvrLoading) return <Spin size="large" className="flex justify-center mt-20" />;

	const nvr = nvrData?.data;
	if (!nvr) return <div>NVR topilmadi</div>;

	const cameras = camerasData?.data?.cameras || [];
	const stateColor: Record<string, string> = { online: "green", offline: "red", unknown: "default" };
	const typeColor: Record<string, string> = { hall: "blue", stage: "purple" };

	const cameraColumns = [
		{ title: "ID", dataIndex: "id", width: 60 },
		{ title: "Nomi", dataIndex: "name" },
		{ title: "Kanal", dataIndex: "channel" },
		{ title: "IP", dataIndex: "ip" },
		{ title: "Serial", dataIndex: "serial", ellipsis: true },
		{
			title: "Turi", dataIndex: "camera_type",
			render: (v: string) => <Tag color={typeColor[v]}>{v === "hall" ? "Zal" : "Sahna"}</Tag>,
		},
		{
			title: "Amallar", width: 150,
			render: (_: unknown, r: Record<string, unknown>) => (
				<Space>
					<Button size="small" icon={<CameraOutlined />} onClick={() => captureMutation.mutate(r.id as number)} title="Screenshot" />
					<Button size="small" icon={<EditOutlined />} onClick={() => openEditCamera(r)} />
					<Popconfirm title="O'chirish?" onConfirm={() => deleteCameraMutation.mutate(r.id as number)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">{nvr.name}</Title>
				<Space>
					<Button icon={<SyncOutlined />} onClick={() => syncMutation.mutate()} loading={syncMutation.isPending}>
						Sinxronlash
					</Button>
				</Space>
			</div>

			<Row gutter={16} className="mb-4">
				<Col span={24}>
					<Card title="NVR ma'lumotlari">
						<Descriptions column={3} bordered size="small">
							<Descriptions.Item label="Nomi">{nvr.name}</Descriptions.Item>
							<Descriptions.Item label="IP">{nvr.ip || "—"}</Descriptions.Item>
							<Descriptions.Item label="Port">{nvr.port || "—"}</Descriptions.Item>
							<Descriptions.Item label="Brand">{nvr.brand || "—"}</Descriptions.Item>
							<Descriptions.Item label="Model">{nvr.model || "—"}</Descriptions.Item>
							<Descriptions.Item label="Serial">{nvr.serial || "—"}</Descriptions.Item>
							<Descriptions.Item label="Device ID">{nvr.device_id || "—"}</Descriptions.Item>
							<Descriptions.Item label="Kanal soni">{nvr.channel_count || "—"}</Descriptions.Item>
							<Descriptions.Item label="Holat">
								<Tag color={stateColor[nvr.state]}>{nvr.state}</Tag>
							</Descriptions.Item>
							<Descriptions.Item label="Sinxron">
								<Tag color={nvr.is_sync ? "green" : "orange"}>{nvr.is_sync ? "Ha" : "Yo'q"}</Tag>
							</Descriptions.Item>
							<Descriptions.Item label="Sinxron vaqti">{nvr.sync_at || "—"}</Descriptions.Item>
							<Descriptions.Item label="To'yxona">
								{nvr.wedding_hall?.name || nvr.weddingHall?.name || "—"}
							</Descriptions.Item>
						</Descriptions>
					</Card>
				</Col>
			</Row>

			{/* ==================== Kameralar ==================== */}
			<Card
				title={`Kameralar (${cameras.length})`}
				extra={
					<Button type="primary" icon={<PlusOutlined />} onClick={openCreateCamera}>
						Kamera qo'shish
					</Button>
				}
			>
				<Table
					columns={cameraColumns}
					dataSource={cameras}
					rowKey="id"
					loading={camerasLoading}
					pagination={false}
					size="small"
				/>
			</Card>

			{/* ==================== Camera Drawer ==================== */}
			<Drawer
				title={editingCameraId ? "Kamera tahrirlash" : "Yangi kamera"}
				open={drawerOpen}
				onClose={closeDrawer}
				width={480}
				extra={
					<Space>
						<Button onClick={closeDrawer}>Bekor qilish</Button>
						<Button type="primary" onClick={() => form.submit()} loading={saveCameraMutation.isPending}>
							Saqlash
						</Button>
					</Space>
				}
			>
				<Form form={form} layout="vertical" onFinish={(v) => saveCameraMutation.mutate(v)}>
					<Form.Item name="name" label="Nomi" rules={[{ required: true }]}>
						<Input placeholder="Zal kamerasi" />
					</Form.Item>
					<Form.Item name="channel" label="Kanal raqami">
						<InputNumber className="w-full" placeholder="1" />
					</Form.Item>
					<Form.Item name="camera_type" label="Turi" initialValue="hall">
						<Select options={[
							{ value: "hall", label: "Zal (odam sanash)" },
							{ value: "stage", label: "Sahna (sanatkor aniqlash)" },
						]} />
					</Form.Item>
					<Form.Item name="serial" label="Serial raqam">
						<Input />
					</Form.Item>
					<Form.Item name="ip" label="IP manzil">
						<Input placeholder="192.168.1.101" />
					</Form.Item>
					<Form.Item name="resolution" label="Sifat">
						<Input placeholder="1080p" />
					</Form.Item>
					<Form.Item name="location" label="Joylashuv">
						<Input placeholder="Asosiy zal, 2-qavat" />
					</Form.Item>
				</Form>
			</Drawer>
		</div>
	);
}
